import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { usePageLoading } from '../contexts/PageLoadingContext';

/**
 * Formata data do formato ISO para o formato português "11 de Mar de 2026"
 * Usa métodos UTC para evitar problemas de timezone
 */
function formatDateLong(dateStr: string): string {
  if (!dateStr) return '';
  
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const date = new Date(dateStr + 'T12:00:00');
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  
  return `${day} de ${month} de ${year}`;
}

/**
 * Estrutura de dados retornada pelo endpoint /consumo/gas/:id/api_data
 * Dados diretos da API externa sem processamento complexo
 */
interface ApiDataResponse {
  customer_id?: string;
  meter_types: string[];
  resume?: {
    last_statement: string;
    next_statement: string;
    days_count: number;
    current_usage: number;
    forecast: number;
    current_month_usage_avg: number;
    monthly_consumption_avg: number;
  };
  usage_today?: number;
  goal_status?: 'Abaixo' | 'Dentro' | 'Ultrapassou' | 'Sem dados';
  monthly_labels?: string[];
  monthly_consumption?: number[];
  daily_usage?: Array<{ date: string; meter_type: string; usage: number }>;
  last_statements?: Array<{ period: string; consumption: number }>;
  /** true quando a unidade não tem medidor deste tipo (ver ConsumptionDataController). */
  no_data?: boolean;
}

/**
 * Dados processados para a GasHomePage
 */
export interface GasConsumptionData {
  // Dados do dia
  usageToday: number;
  currentMonthUsageAvg: number;
  
  // Dados do mês
  currentUsage: number;
  monthlyConsumptionAvg: number;
  forecast: number;
  
  // Meta
  goalStatus: 'Abaixo' | 'Dentro' | 'Ultrapassou' | 'Sem dados';
  hasGoal: boolean;
  goalValue: number;
  
  // Próximo fechamento
  nextStatement: string;
  beginningOfPeriod: string;
  
  // Gráfico mensal (últimos 6 meses)
  monthlyLabels: string[];
  monthlyConsumption: number[];
  
  // Gráfico diário
  dailyLabels: number[];
  gasDaily: number[];
  
  // Tipos de medidor
  meterTypes: string[];
}

interface UseGasConsumptionResult {
  data: GasConsumptionData | null;
  isLoading: boolean;
  error: string | null;
  /** true quando a unidade não tem medidor de gás cadastrado (não é um erro). */
  noData: boolean;
  refetch: () => void;
}

/**
 * Hook para buscar dados de consumo de gás de uma residência.
 *
 * Chama o endpoint: GET /consumo/gas/:rgi/api_data
 *
 * @param residenceId - ID da residência (RGI)
 */
export function useGasConsumption(residenceId: string): UseGasConsumptionResult {
  const [data, setData] = useState<GasConsumptionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noData, setNoData] = useState(false);
  const { startPageLoading, stopPageLoading } = usePageLoading();

  // Esconde o preloader apenas APÓS o React processar isLoading = false
  useEffect(() => {
    if (!isLoading) {
      stopPageLoading();
    }
  }, [isLoading, stopPageLoading]);

  const fetchData = useCallback(async () => {
    if (!residenceId) {
      setError('ID da residência não fornecido');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    startPageLoading();
    setError(null);
    setNoData(false);

    try {
      // Usa a rota api_data que retorna dados da API de forma simples
      const response = await api.get<ApiDataResponse>(
        `/consumo/gas/${residenceId}/api_data`
      );

      const apiData = response.data;

      if (apiData.no_data) {
        setData(null);
        setNoData(true);
        return;
      }

      // Extrai dados do consumo diário de gás
      const dailyUsage = apiData.daily_usage || [];
      const dailyLabels = dailyUsage.map(d => new Date(d.date).getDate());
      const gasDaily = dailyUsage.map(d => d.usage);

      const resume = apiData.resume || {} as NonNullable<ApiDataResponse['resume']>;

      const processedData: GasConsumptionData = {
        usageToday: apiData.usage_today || 0,
        currentMonthUsageAvg: resume.current_month_usage_avg,
        currentUsage: resume.current_usage,
        monthlyConsumptionAvg: resume.monthly_consumption_avg,
        forecast: resume.forecast,
        goalStatus: apiData.goal_status || 'Dentro',
        hasGoal: false, // API não retorna isso diretamente
        goalValue: resume.monthly_consumption_avg,
        nextStatement: resume.next_statement,
        beginningOfPeriod: formatDateLong(resume.last_statement),
        monthlyLabels: apiData.monthly_labels || [],
        monthlyConsumption: apiData.monthly_consumption || [],
        dailyLabels,
        gasDaily,
        meterTypes: apiData.meter_types,
      };

      setData(processedData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar dados de consumo de gás';
      setError(errorMessage);
      console.error('useGasConsumption error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [residenceId, startPageLoading]);

  // Busca dados quando o residenceId muda
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    noData,
    refetch: fetchData,
  };
}
